/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MenuItem } from '@wit/vtv-x-foundation-lib';
import LocalizedStrings from 'react-localization';
import { ESearchFilter, ISearchFilter } from 'types/components/Search';
import Constants from 'configs/Constants';
import { Button } from '..';
import translate from '../../../services/i18n/translate';
import * as SearchDuck from '../../../store/ducks/search';
import { ButtonType } from '../Button';

interface ISearchFilterProps {
  filtersRecom: MenuItem[];
  filters: ISearchFilter[];
  isRecomendations: boolean;
  consent: boolean;
  optInCallback(active: boolean): void;
}

const SearchFilterList: React.FunctionComponent<ISearchFilterProps> = (props) => {
  const { filters, filtersRecom, isRecomendations, consent, optInCallback } = props;
  const dispatch = useDispatch();

  const onClickHandler = useCallback(
    (filter: string, menuItemId?: number) => {
      dispatch(SearchDuck.ActionCreators.setActiveFilter({ filterKey: filter, menuItemId }, isRecomendations, consent));
      if (filter !== ESearchFilter.OPT_IN) {
        optInCallback(false);
        dispatch(SearchDuck.ActionCreators.searchAssets(Constants.MAX_ASSETS_PER_RAIL_SEARCH_RESULTS));
      } else {
        optInCallback(true);
      }
    },
    [consent, dispatch, isRecomendations, optInCallback]
  );
  const activeFilter = useSelector(SearchDuck.Selectors.getActiveFilter) || 'searchRecommendations1';
  const recommActiveFilter = Number(activeFilter.filterKey) || 0;

  // Filters for Non Recommendations, or for Recommendations active and Consent inactive
  const FiltersForNonRecom = useCallback(() => {
    let arrayTemp: ISearchFilter[] = filters;
    if (isRecomendations) {
      // Environment has Recomendations - Show: ALL / OPTIN
      if (!consent) {
        arrayTemp = [
          {
            key: 'all',
            type: ESearchFilter.ALL,
            translationKey: 'search.searchRecommendations1',
          },
          {
            key: 'optIn',
            type: ESearchFilter.OPT_IN,
            translationKey: 'search.optInRecommendations',
          },
        ];
      } else {
        return null;
      }
    } else {
      // Environment doesnt have Recomendations - Show: ALL / ON TV / ON DEMAND
      arrayTemp = filters;
    }

    return (
      <>
        {arrayTemp.map((filter: ISearchFilter) => {
          // Get translation text
          const getLabelForButton = translate[`${filter.translationKey}` as keyof typeof LocalizedStrings];

          // Only return the Button if translation exists
          return (
            getLabelForButton && (
              <Button
                buttonType={ButtonType.TAB}
                onClick={() => {
                  if (activeFilter.filterKey !== filter.type) {
                    onClickHandler(filter.type || '');
                  }
                }}
                key={filter.key}
                bemBlock="SearchFilters"
                bemModifier={filter.key}
                text={getLabelForButton}
                active={activeFilter.filterKey === filter.type}
              />
            )
          );
        })}
      </>
    );
  }, [filters, isRecomendations, consent, activeFilter, onClickHandler]);

  // Filters for Recommendations and Consent active
  const FiltersForRecom = useCallback(() => {
    if (isRecomendations && consent) {
      return (
        <>
          {filtersRecom.map((item: MenuItem, index: number) => {
            const getLabelForButton = translate[`searchRecommendationsN${index}` as keyof typeof LocalizedStrings];
            const isActive = recommActiveFilter === index;
            return (
              <Button
                buttonType={ButtonType.TAB}
                onClick={() => {
                  if (!isActive) {
                    onClickHandler(index.toString(), item.id);
                  }
                }}
                key={item.id}
                bemBlock="SearchFilters"
                bemModifier={item.id}
                text={getLabelForButton || item.name}
                active={isActive}
              />
            );
          })}
        </>
      );
    }
    return null;
  }, [consent, filtersRecom, isRecomendations, onClickHandler, recommActiveFilter]);

  return (
    <>
      <div className="btn-group search-filter" aria-hidden="false" id="SearchFilters_wrapper">
        <FiltersForRecom />
        <FiltersForNonRecom />
      </div>
    </>
  );
};

export default SearchFilterList;
